import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

async function decodeBlobErrorBody(err: unknown) {
  if (!axios.isAxiosError(err) || !err.response) return;
  const data = err.response.data;
  if (typeof Blob === 'undefined' || !(data instanceof Blob)) return;
  try {
    const text = await data.text();
    const trimmed = text.trim();
    if (!trimmed) return;
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      err.response.data = JSON.parse(trimmed);
    }
  } catch {
    /* keep the blob; getErrorMessage will fall back to the status code */
  }
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    await decodeBlobErrorBody(err);
    if (err.response?.status === 401) {
      const refresh = localStorage.getItem('refresh_token');
      if (refresh && !err.config?._retry) {
        try {
          const { data } = await axios.post(`${API_BASE}/auth/refresh/`, { refresh });
          localStorage.setItem('access_token', data.access);
          if (data.refresh) localStorage.setItem('refresh_token', data.refresh);
          err.config._retry = true;
          err.config.headers.Authorization = `Bearer ${data.access}`;
          return api.request(err.config);
        } catch {
          /* fall through */
        }
      }
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      if (!window.location.pathname.startsWith('/')) window.location.href = '/';
      else if (window.location.pathname !== '/') window.location.href = '/';
    }
    return Promise.reject(err);
  },
);

export function isApiAssetUrl(path: string): boolean {
  return path.startsWith('/api/') || path.startsWith('api/');
}

export function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function getMediaUrl(path: string | undefined): string {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  if (path.startsWith('/assets/')) return path;

  const baseUrl = API_BASE.replace(/\/api\/?$/, '');
  const token = localStorage.getItem('access_token');

  if (isApiAssetUrl(path)) {
    const apiPath = path.startsWith('/') ? path : `/${path}`;
    let url = `${baseUrl}${apiPath}`;
    if (token) url += `${url.includes('?') ? '&' : '?'}access_token=${encodeURIComponent(token)}`;
    return url;
  }

  let relative = path.startsWith('/') ? path.slice(1) : path;
  if (relative.startsWith('media/')) relative = relative.slice('media/'.length);

  const encoded = relative.split('/').map((s) => encodeURIComponent(s)).join('/');
  let url = `${baseUrl}/api/media/${encoded}`;
  if (token) url += `${url.includes('?') ? '&' : '?'}access_token=${encodeURIComponent(token)}`;
  return url;
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ApiUser {
  id: string;
  email: string;
  full_name: string;
  role: 'admin' | 'doctor';
  role_name: string;
  specialization: string | null;
  phone: string | null;
  is_active: boolean;
  created_at: string;
  active_assignments?: number;
}

export interface LoginPayload {
  email: string;
  password: string;
  role?: string;
}

export interface AuthResponse {
  user: ApiUser;
  access: string;
  refresh: string;
}

export type FieldErrors = Record<string, string[]>;

export function extractFieldErrors(error: unknown): FieldErrors | null {
  if (axios.isAxiosError(error) && error.response?.status === 400) {
    return error.response.data as FieldErrors;
  }
  return null;
}

export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    const isBlob = typeof Blob !== 'undefined' && data instanceof Blob;
    if (data && typeof data === 'object' && !isBlob) {
      if (typeof (data as { detail?: string }).detail === 'string') return (data as { detail: string }).detail;
      const fieldErrs = data as Record<string, string[]>;
      const first = Object.values(fieldErrs).flat()[0];
      if (typeof first === 'string' && first) return first;
    }
    if (error.code === 'ERR_NETWORK') {
      return 'Upload failed — connection lost (batch may be too large or the server timed out). Try again after restarting the backend.';
    }
    if (error.response?.status === 413) {
      return 'Upload batch is too large. Try fewer images per batch or increase server upload limits.';
    }
    if (error.response?.status === 507) {
      return 'Object storage is full or unavailable. Free disk space on the server (MinIO volume), then retry.';
    }
    if (error.response?.status === 503) {
      return 'A required service is unavailable (database, Redis, or storage). Check server health and try again.';
    }
    if (error.response?.status) return `Request failed (${error.response.status})`;
  }
  return 'An unexpected error occurred';
}

// ─── Auth ───────────────────────────────────────────────────────────────────

export const authApi = {
  login: (data: LoginPayload) => api.post<AuthResponse>('/auth/login/', data),
  logout: (refresh: string) => api.post('/auth/logout/', { refresh }),
  me: () => api.get<ApiUser>('/auth/me/'),
  changePassword: (data: { current_password: string; new_password: string }) =>
    api.post('/auth/change-password/', data),
};

// ─── Doctors (admin) ────────────────────────────────────────────────────────

export interface DoctorAssignment {
  id: string;
  sample_id: string | null;
  sample_name: string;
  total_patches: number;
  status: 'pending' | 'in_review' | 'submitted';
  assigned_at: string;
  completed_at?: string | null;
}

export interface DoctorAssignmentHistoryResponse {
  assigned_count: number;
  submitted_count: number;
  assignments: DoctorAssignment[];
}

export const doctorsApi = {
  list: () => api.get<{ results?: ApiUser[] } | ApiUser[]>('/doctors/'),
  detail: (id: string) => api.get<ApiUser>(`/doctors/${id}/`),
  assignments: (id: string) =>
    api.get<DoctorAssignmentHistoryResponse>(`/doctors/${id}/assignments/`),
  create: (data: {
    email: string;
    password: string;
    full_name: string;
    specialization?: string;
    phone?: string;
  }) => api.post<ApiUser>('/doctors/', data),
  update: (id: string, data: Partial<{
    email: string;
    full_name: string;
    specialization: string;
    phone: string;
    is_active: boolean;
    password: string;
  }>) => api.patch<ApiUser>(`/doctors/${id}/`, data),
  delete: (id: string) => api.delete(`/doctors/${id}/`),
  /** @deprecated use delete */
  deactivate: (id: string) => api.delete(`/doctors/${id}/`),
};

// ─── Samples (admin) ────────────────────────────────────────────────────────

export interface Sample {
  id: string;
  sample_name: string;
  total_patches: number;
  upload_date: string;
  status: 'unassigned' | 'in_review' | 'reconciling' | 'completed';
  assignment_count?: number;
  review_display?: string;
  patches?: Patch[];
  final_assignment_id?: string | null;
  reconciled_at?: string | null;
  has_conflicts?: boolean | null;
}

export interface CompletedSample {
  id: string;
  sample_name: string;
  total_patches: number;
  reconciled_at: string | null;
  upload_date: string | null;
  doctors: { id: string; full_name: string; email: string }[];
  sample_deleted?: boolean;
}

export interface Patch {
  id: string;
  patch_identifier: string;
  filename: string;
  image_path: string;
  width: number;
  height: number;
  upload_date?: string;
  review_status?: string;
  is_modified?: boolean;
}

export interface Assignment {
  id: string;
  sample_id: string;
  sample_name: string;
  total_patches: number;
  assigned_at: string;
  status: 'pending' | 'in_review' | 'submitted';
  admin_note?: string;
  doctor_note?: string;
  is_final?: boolean;
  completed_at?: string | null;
  doctor_id?: string;
  doctor_name?: string;
}

export const samplesApi = {
  list: (params?: Record<string, string>) => api.get<{ results: Sample[]; count: number } | Sample[]>('/samples/', { params }),
  detail: (id: string) => api.get<Sample>(`/samples/${id}/`),
  patches: (id: string) => api.get<Patch[]>(`/samples/${id}/patches/`),
  bulkDelete: (sample_ids: string[]) =>
    api.post<{ detail: string; deleted: number; sample_names: string[]; media_errors?: string[] }>(
      '/samples/bulk-delete/',
      { sample_ids },
    ),
  bulkAssign: (data: { sample_ids: string[]; doctor_ids: string[]; admin_note?: string }) =>
    api.post<{ detail: string; assigned_samples: number; created: number; sample_names: string[] }>(
      '/samples/bulk-assign/',
      data,
    ),
  bulkExport: (data: { sample_ids: string[]; fmt: 'xml' | 'json' | 'npy'; include_images?: boolean }) =>
    api.post('/samples/bulk-export/', data, { responseType: 'blob' }),
  completed: (params?: Record<string, string>) =>
    api.get<{ results: CompletedSample[]; count: number } | CompletedSample[]>(
      '/samples/completed/',
      { params },
    ),
  assign: (id: string, data: { doctor_ids: string[]; admin_note?: string }) =>
    api.post(`/samples/${id}/assign/`, data),
  assignments: (id: string) => api.get<Assignment[]>(`/samples/${id}/assignments/`),
  export: (id: string, format: 'xml' | 'json' | 'npy', includeImages = false) =>
    api.get(`/samples/${id}/export/`, {
      params: { fmt: format, ...(includeImages ? { include_images: '1' } : {}) },
      responseType: includeImages || format !== 'json' ? 'blob' : 'json',
    }),
};

// ─── Upload (admin) ─────────────────────────────────────────────────────────

export interface UploadJobStatus {
  job_id: string;
  status: string;
  total_files: number;
  processed_files: number;
  duplicate_samples: string[];
  samples_created: number;
  patches_added: number;
  error_message: string | null;
}

export const uploadApi = {
  readiness: () =>
    api.get<{
      ready: boolean;
      storage: { ok: boolean; detail: string; backend?: string };
      warnings: string[];
    }>('/upload/readiness/'),
  create: (images: File[], annotationsFiles: File[]) => {
    const form = new FormData();
    images.forEach((f) => form.append('images[]', f));
    annotationsFiles.forEach((f) => form.append('annotations_files[]', f));
    return api.post<{ job_id: string }>('/upload/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600_000,
    });
  },
  status: (jobId: string) => api.get<UploadJobStatus>(`/upload/${jobId}/status/`),
  confirm: (jobId: string, merge_decisions: Record<string, 'merge' | 'skip'>) =>
    api.post(`/upload/${jobId}/confirm/`, { merge_decisions }),
  cancel: (jobId: string) => api.delete(`/upload/${jobId}/`),
};

// ─── Assignments ──────────────────────────────────────────────────────────────

export interface AnnotationPayload {
  shape_type: 'bbox' | 'polygon';
  coordinates: Record<string, unknown>;
  label: string;
  color?: string;
  attributes?: Record<string, string>;
}

export interface ViewerAnnotation {
  id?: string;
  shape_type: 'bbox' | 'polygon';
  coordinates: { points?: number[][]; xtl?: number; ytl?: number; xbr?: number; ybr?: number };
  label: string;
  color: string;
  attributes?: Record<string, string>;
}

export const assignmentsApi = {
  list: (params?: Record<string, string>) =>
    api.get<{ results: Assignment[] } | Assignment[]>('/assignments/', { params }),
  historyStats: () =>
    api.get<{ assigned: number; submitted: number; pending: number; in_review: number }>(
      '/assignments/history-stats/',
    ),
  detail: (id: string) => api.get<Assignment>(`/assignments/${id}/`),
  patches: (id: string) => api.get<Patch[]>(`/assignments/${id}/patches/`),
  note: (id: string, doctor_note: string) =>
    api.patch(`/assignments/${id}/note/`, { doctor_note }),
  submit: (id: string) => api.post(`/assignments/${id}/submit/`),
  review: (id: string) => api.get(`/assignments/${id}/review/`),
  unassign: (id: string) => api.delete(`/assignments/${id}/unassign/`),
  getAnnotations: (assignmentId: string, patchId: string) =>
    api.get<{
      source: string;
      annotations: ViewerAnnotation[];
      original_annotations?: ViewerAnnotation[];
      doctor_annotations?: ViewerAnnotation[];
      is_modified?: boolean;
      review_status?: string;
      patch: Patch;
    }>(`/assignments/${assignmentId}/patches/${patchId}/annotations/`),
  saveAnnotations: (assignmentId: string, patchId: string, annotations: AnnotationPayload[]) =>
    api.post(`/assignments/${assignmentId}/patches/${patchId}/annotations/save/`, { annotations }),
};

// ─── Comparison & reconciliation (admin) ────────────────────────────────────

export interface ComparisonPatchSummary {
  patch_id: string;
  patch_identifier: string;
  filename: string;
  image_path: string;
  conflicts: number;
  agreed: number;
  total_cells: number;
  is_resolved: boolean;
}

export interface ComparisonOverview {
  sample_id: string;
  sample_name: string;
  status: string;
  reconciled_at: string | null;
  doctors: string[];
  patches: ComparisonPatchSummary[];
}

export interface ComparisonParty {
  party_id: string;
  party_name: string;
}

export interface ComparisonMember {
  party_id: string;
  party_name: string;
  present: boolean;
  label: string | null;
  sublabel: string | null;
  color: string | null;
  shape_type: 'bbox' | 'polygon' | null;
  coordinates: ViewerAnnotation['coordinates'] | null;
  attributes: Record<string, string> | null;
}

export interface ComparisonCluster {
  cluster_id: number;
  type: 'agreed' | 'label_conflict' | 'presence';
  shape_type: 'bbox' | 'polygon';
  coordinates: ViewerAnnotation['coordinates'];
  color: string;
  agreed_label: string | null;
  members: ComparisonMember[];
}

export interface ComparisonPatchDetail {
  patch: Patch;
  parties: ComparisonParty[];
  clusters: ComparisonCluster[];
  final_annotations: ViewerAnnotation[];
  is_resolved: boolean;
}

export const comparisonApi = {
  overview: (sampleId: string) =>
    api.get<ComparisonOverview>(`/samples/${sampleId}/comparison/`),
  patch: (sampleId: string, patchId: string) =>
    api.get<ComparisonPatchDetail>(`/samples/${sampleId}/comparison/patches/${patchId}/`),
  notify: (sampleId: string, patchId?: string) =>
    api.post(`/samples/${sampleId}/comparison/notify/`, patchId ? { patch_id: patchId } : {}),
  finalize: (sampleId: string) =>
    api.post(`/samples/${sampleId}/finalize/`),
};

export const doctorComparisonApi = {
  overview: (assignmentId: string) =>
    api.get<ComparisonOverview>(`/assignments/${assignmentId}/comparison/`),
  patch: (assignmentId: string, patchId: string) =>
    api.get<ComparisonPatchDetail>(`/assignments/${assignmentId}/comparison/patches/${patchId}/`),
  resolve: (assignmentId: string, patchId: string, annotations: AnnotationPayload[]) =>
    api.post(`/assignments/${assignmentId}/comparison/patches/${patchId}/resolve/`, { annotations }),
  finalize: (assignmentId: string) =>
    api.post(`/assignments/${assignmentId}/comparison/finalize/`),
};

// ─── Notifications ────────────────────────────────────────────────────────────

export interface AppNotification {
  id: string;
  type: string;
  message: string;
  is_read: boolean;
  sample_id: string;
  assignment_id: string | null;
  created_at: string;
}

export const notificationsApi = {
  list: (unreadOnly?: boolean) =>
    api.get<AppNotification[] | { results: AppNotification[] }>('/notifications/', {
      params: unreadOnly ? { unread: 'true' } : {},
    }),
  unreadCount: () => api.get<{ count: number }>('/notifications/unread-count/'),
  markRead: (id: string) => api.patch(`/notifications/${id}/read/`),
  markAllRead: () => api.post('/notifications/mark-all-read/'),
  markReadByContext: (payload: { assignment_id?: string; sample_id?: string }) =>
    api.post<{ marked_read: number }>('/notifications/mark-read-by-context/', payload),
};

/** Fired after notifications are marked read outside the bell (e.g. opening a case). */
export const NOTIFICATIONS_CHANGED_EVENT = 'notifications:changed';

export function emitNotificationsChanged() {
  window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED_EVENT));
}

/** Mark matching unread notifications when a case/assignment is opened (Eye or deep link). */
export async function markNotificationsForOpenContext(payload: {
  assignment_id?: string;
  sample_id?: string;
}) {
  if (!payload.assignment_id && !payload.sample_id) return;
  try {
    await notificationsApi.markReadByContext(payload);
    emitNotificationsChanged();
  } catch {
    /* ignore — bell will refresh on next poll */
  }
}

export default api;
